import { computed, type MaybeRefOrGetter, toValue } from 'vue'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/vue-query'
import { http, isApiError, unwrap } from './client'
import type {
  AdminWorkspace,
  AdminWorkspaces,
  ApiEnvelope,
  DataProvenanceMode,
  EntitlementStatus,
  SeatStatus,
  WorkspaceAccess,
  WorkspaceMember,
  WorkspaceMembers,
} from '@/types/api'

/**
 * A workspace's API path. The id comes from the page's URL, which the router
 * has decoded, so it is encoded again: `..%2Fadmin` must not become a path of
 * its own.
 */
export const workspacePath = (workspaceId: string): string => `/workspaces/${encodeURIComponent(workspaceId)}`

export const workspaceKeys = {
  mine: ['workspaces'] as const,
  one: (id: string) => ['workspaces', id] as const,
  members: (id: string) => ['workspaces', id, 'members'] as const,
  admin: ['admin', 'workspaces'] as const,
}

/**
 * A refusal (not a member, not switched on, no seat, viewing as someone) is
 * an answer, not a failure worth asking again.
 */
export const retryUnlessRefused = (failures: number, error: unknown): boolean =>
  !(isApiError(error) && [403, 404].includes(error.statusCode)) && failures < 1

/** The workspaces the signed-in person can enter Intelligence in. */
export const useMyWorkspaces = (enabled: MaybeRefOrGetter<boolean> = true) =>
  useQuery({
    queryKey: workspaceKeys.mine,
    queryFn: async () => unwrap((await http.get<ApiEnvelope<WorkspaceAccess[]>>('/workspaces')).data),
    enabled: computed(() => toValue(enabled)),
    retry: retryUnlessRefused,
  })

const fetchWorkspace = async (workspaceId: string): Promise<WorkspaceAccess> =>
  unwrap((await http.get<ApiEnvelope<WorkspaceAccess>>(workspacePath(workspaceId))).data)

/** What this person can do in one workspace; refused (403/404) if nothing. */
export const useWorkspace = (workspaceId: MaybeRefOrGetter<string | undefined>) =>
  useQuery({
    queryKey: computed(() => workspaceKeys.one(toValue(workspaceId) ?? '')),
    queryFn: () => fetchWorkspace(toValue(workspaceId) ?? ''),
    enabled: computed(() => !!toValue(workspaceId)),
    retry: retryUnlessRefused,
  })

/** The same question outside a component (the router), answered from the same cache entry. */
export const loadWorkspace = (queryClient: QueryClient, workspaceId: string): Promise<WorkspaceAccess> =>
  queryClient.fetchQuery({
    queryKey: workspaceKeys.one(workspaceId),
    queryFn: () => fetchWorkspace(workspaceId),
    retry: retryUnlessRefused,
    staleTime: 60_000,
  })

export const useWorkspaceMembers = (workspaceId: MaybeRefOrGetter<string>) =>
  useQuery({
    queryKey: computed(() => workspaceKeys.members(toValue(workspaceId))),
    queryFn: async () =>
      unwrap((await http.get<ApiEnvelope<WorkspaceMembers>>(`${workspacePath(toValue(workspaceId))}/members`)).data),
    retry: retryUnlessRefused,
  })

/** The Owner grants (ENABLED) or revokes (REVOKED) a member's seat. */
export const useSetSeat = (workspaceId: MaybeRefOrGetter<string>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, status }: { userId: string; status: SeatStatus }) =>
      unwrap(
        (
          await http.put<ApiEnvelope<WorkspaceMember>>(
            `${workspacePath(toValue(workspaceId))}/members/${encodeURIComponent(userId)}/intelligence-access`,
            { status },
          )
        ).data,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: workspaceKeys.members(toValue(workspaceId)) }),
  })
}

// ─── Settings → Workspaces (Elfie admins) ───────────────────────────────────

export const useAdminWorkspaces = () =>
  useQuery({
    queryKey: workspaceKeys.admin,
    queryFn: async () => unwrap((await http.get<ApiEnvelope<AdminWorkspaces>>('/admin/workspaces')).data),
  })

/** Any change here changes who can enter which workspace: refresh both views. */
const useInvalidateWorkspaces = () => {
  const queryClient = useQueryClient()
  return async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: workspaceKeys.admin }),
      queryClient.invalidateQueries({ queryKey: workspaceKeys.mine }),
    ])
  }
}

export const useSwitchOnWorkspace = () => {
  const invalidate = useInvalidateWorkspaces()
  return useMutation({
    mutationFn: async (payload: { workspaceId: string; dataProvenanceMode: DataProvenanceMode }) =>
      unwrap((await http.post<ApiEnvelope<AdminWorkspace>>('/admin/workspaces', payload)).data),
    onSuccess: invalidate,
  })
}

export const useChangeWorkspaceStatus = () => {
  const invalidate = useInvalidateWorkspaces()
  return useMutation({
    mutationFn: async ({ workspaceId, status, reason }: { workspaceId: string; status: EntitlementStatus; reason?: string }) =>
      unwrap(
        (await http.put<ApiEnvelope<AdminWorkspace>>(`/admin/workspaces/${workspaceId}/status`, { status, reason })).data,
      ),
    onSuccess: invalidate,
  })
}

export const useSyncWorkspaces = () => {
  const invalidate = useInvalidateWorkspaces()
  return useMutation({
    mutationFn: async () => unwrap((await http.post<ApiEnvelope<unknown>>('/admin/workspaces/sync')).data),
    onSuccess: invalidate,
  })
}
